import type { ElementRef } from '../../services/elements';
import type { UploadProgress } from '../../services/storageUpload';

export type MediaKind = 'image' | 'video';
export interface PixelSize { width: number; height: number }
export interface Reference extends PixelSize {
  id: string; name: string; url: string; mime: string;
  mediaAssetId?: string; objectId?: string; origin: 'canvas' | 'upload' | 'element';
  element?: ElementRef; elementImageCount?: number; elementKind?: string;
  mediaKind?: 'image' | 'video' | 'audio'; duration?: number;
  thumbnailUrl?: string;
  /** A drawn layer or artboard's size on the page; `width` and `height` are the file's, up to twice that. */
  canvasSize?: PixelSize;
  role?: 'first' | 'last' | 'image' | 'video' | 'audio';
}
export interface Model {
  id: string; name: string; kind: MediaKind; ratios: string[]; qualities: string[];
  durations: string[]; modes: string[]; maxReferences: number; maxVariations: number;
  customSize: boolean; customSizeLimits?: { minPixels: number; maxPixels: number; maxEdge: number; multiple: number };
  imageInputRole?: string;
  provider?: string;
  icon?: string;
  featured?: boolean;
  description?: string;
  audio?: boolean[];
  thinkingLevels?: string[];
  transparent?: boolean;
  maxInputImageBytes?: number;
  durationMaxWhen?: { key: string; value: string; max: number }[];
  /** Video modes on which the provider refuses an empty prompt (ai-back `promptRequiredRoutes`). Absent = undeclared. */
  promptRequiredModes?: ('text' | 'first' | 'firstLast' | 'reference')[];
  /** The shared catalogue schema; video port meanings cannot be inferred from a flat count. */
  raw?: Record<string, unknown>;
}
export interface Request {
  prompt: string; kind: MediaKind; model: string; quantity: number;
  inputs: Record<string, unknown>; references: Reference[];
  video?: { side: 'frames' | 'references' };
  /** A new result belongs to the file/page. Only an existing fill names a server target. */
  destination?: { documentId: string; pageId: string; targetId?: string;
    /** Which surface asked (nodes-back): `generate` = the Generate panel's own prompt, which Taste reads. */
    surface?: 'generate' | 'tool' };
}
export interface Result extends PixelSize {
  mediaAssetId: string; driveFileId?: string; url: string; thumbnailUrl?: string;
  mime: string; name: string; duration?: number;
}
export interface Run {
  id: string; aiJobId?: string; status: string; results: Result[];
  progress?: number; userMessage?: string;
  /** A layerize run's layer document (text, boxes, fonts); its pictures are `results` by index. */
  document?: unknown;
}
/** A picture or clip tool's run (imageTools): which tool, and how its result lands on the layer. */
export interface ToolRun {
  id: string; label: string; land: string;
  /** The picture as it was sent (its pixels): layers made from it come back on a board this size. */
  source?: PixelSize;
  /** Focus: how soft the background is made (Light · Natural · Strong) when it is composed. */
  strength?: 'light' | 'natural' | 'strong';
  /**
   * The layer was sent as it looks (its effects, extra fills and stroke drawn in), so the result
   * already wears them: it lands as the layer's one picture, and those are switched off.
   */
  flattened?: boolean;
}
export interface Binding {
  key: string; documentId: string; pageId: string; targetId: string; targetName: string;
  request: Request; createdAt: number; runId?: string; aiJobId?: string;
  status: string; results: Result[]; applied: boolean; message?: string;
  /** A new placeholder can retain failure feedback without covering existing artwork. */
  createdTarget?: boolean;
  /** The quote's estimate, so every viewer of the file draws the same progress curve. */
  etaSeconds?: number;
  /** Present on a tool run (Edit › Tools on a picture or clip): its result lands on `targetId` by `tool.land`. */
  tool?: ToolRun;
  /** A layerize run's layer document, kept with the run until it lands. */
  document?: unknown;
}
/** `label`, when given, is what the button says instead of the price (the tour's "Free in the tour"). */
export interface Quote { credits: number; seconds?: number; label?: string }
/** A live run frame from the Playground socket (`playground:job:*`), by run id. */
export interface RunFrame {
  id: string; event: 'queued' | 'progress' | 'completed' | 'failed' | 'cancelled';
  progress?: number; stage?: string;
}
export interface RunFeed { follow(ids: readonly string[]): void; close(): void }
/**
 * Some or all of how a picture was made, handed to Generate from the Info tab: any part given is
 * put in the composer (prompt, model, settings, references), the rest is left as it is.
 */
export interface Recipe {
  kind?: 'image' | 'video';
  prompt?: string;
  model?: string;
  inputs?: Record<string, unknown>;
  references?: Reference[];
  video?: { side: 'frames' | 'references' };
}
/** `kind: 'video'` (Image to Video on a picture): Generate turns to Video first, then takes the picture as the first frame. */
export type ReferenceIntent = { key: string; ids: readonly string[]; kind?: 'video' } | { key: string; elements: ElementRef[] } | { key: string; recipe: Recipe };
export const referenceImageCount = (refs: Reference[]) => refs.reduce((sum, ref) => sum + (ref.element ? ref.elementImageCount ?? 1 : 1), 0);
export interface GenerationClient {
  library?(options: { kind: 'image' | 'video' | 'audio'; query?: string; cursor?: string; signal?: AbortSignal }): Promise<{ references: Reference[]; cursor: string | null }>;
  elements?(refs: ElementRef[]): Promise<Reference[]>;
  models(kind: MediaKind, references: boolean): Promise<Model[]>;
  recent?(kind: MediaKind, references: boolean): Promise<Model[]>;
  params?(model: Model): Promise<Model>;
  quote(request: Request): Promise<Quote>;
  generate(request: Request): Promise<Run>;
  get(id: string): Promise<Run>;
  /** `stopped: false` = the provider already had it; nothing is charged and the result is discarded. */
  cancel(id: string): Promise<{ cancelled: boolean; stopped?: boolean }>;
  /** Live frames, as Flow's Playground receives them. Without it the panel polls. */
  feed?(onFrame: (frame: RunFrame) => void, onConnection: (connected: boolean) => void): RunFeed;
  reference(blob: Blob, name: string, origin: Reference['origin'], objectId?: string, onProgress?: (progress: UploadProgress) => void): Promise<Reference>;
  /** Words to start an empty prompt with (the AI tour's, lib/tours/freeGeneration). */
  starterPrompt?(): string | null;
}
export const terminal = (status: string) => ['completed', 'failed', 'canceled', 'cancelled'].includes(status);
